import React, { createContext, useLayoutEffect, useState } from 'react';
import _ from 'lodash';
import { THEMES } from 'src/constants';
import { batch, useDispatch } from 'react-redux';

import { clearCheckIns } from 'src/slices/checkins';
import { clearGroups } from 'src/slices/group';
import { clearLocations } from 'src/slices/location';
import { clearParticipants } from 'src/slices/participant';

let preferredTheme;
if (
  window.matchMedia &&
  window.matchMedia('(prefers-color-scheme: dark)').matches
) {
  preferredTheme = THEMES.ONE_DARK;
} else {
  preferredTheme = THEMES.LIGHT;
}

const defaultSettings = {
  direction: 'ltr',
  language: 'nb',
  responsiveFontSizes: true,
  theme: preferredTheme,
  selectedContest: null
};

export const restoreSettings = () => {
  let storedSettings = null;

  try {
    const storedData = window.localStorage.getItem('settings');
    try {
      storedSettings = JSON.parse(storedData);
    } catch (parseErr) {
      // If stored data is not a strigified JSON this will fail.
      // Replace invalid data with default settings.
      console.error(parseErr);
      window.localStorage.setItem('settings', JSON.stringify(defaultSettings));
    }
  } catch (err) {
    // localStorage error
    console.error(err);
  }

  if (storedSettings) {
    return { ...defaultSettings, ...storedSettings };
  }
  return defaultSettings;
};

export const storeSettings = settings => {
  window.localStorage.setItem('settings', JSON.stringify(settings));
};

const SettingsContext = createContext({
  settings: defaultSettings,
  saveSettings: () => {},
  restoreDefaultSettings: () => {}
});

export const SettingsProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [currentSettings, setCurrentSettings] = useState(restoreSettings);

  const handleSaveSettings = (update = {}) => {
    const mergedSettings = _.merge({}, currentSettings, update);

    // If selected contest has changed, clear all redux stores
    if (update.selectedContest) {
      batch(() => {
        dispatch(clearCheckIns());
        dispatch(clearGroups());
        dispatch(clearLocations());
        dispatch(clearParticipants());
      });
    }

    setCurrentSettings(mergedSettings);
    storeSettings(mergedSettings);
  };

  const handleRestoreDefaultSettings = () => {
    setCurrentSettings(defaultSettings);
    storeSettings(defaultSettings);
  };

  useLayoutEffect(() => {
    document.dir = currentSettings.direction;
    document.documentElement.lang = currentSettings.language;
  }, [currentSettings]);

  return (
    <SettingsContext.Provider
      value={{
        settings: currentSettings,
        saveSettings: handleSaveSettings,
        restoreDefaultSettings: handleRestoreDefaultSettings
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const SettingsConsumer = SettingsContext.Consumer;

export default SettingsContext;
